import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiChevronDown } from "react-icons/fi";

import { useDispatch, useSelector } from "react-redux";
import { subscriptionData } from "../../redux/slices/subscriptionSlice";
import { subscriptionPayment } from "../../redux/slices/subscriptionPaymentSlice";
import { refreshUserProfile } from "../../redux/slices/authSlice";

import UpgradeConfirmationModal from "../../pages/registration-steps/upgradeConfirmationModal";
import CancelSubscriptionModal from "./cancelSubscriptionModal";
import {
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import usePageTracking from "../../hooks/usePageTracking";
import { getTrackingData } from "../../utils/trackingConfig";
import GuidedTour from "../../components/tour/GuidedTour";
import useTour from "../../components/tour/useTour";
import getSubscriptionTourSteps from "./subscriptionTourSteps";
import "./SubscriptionMembership.scss";

/* Skeleton placeholder for a pricing card — shown while /subscription-data
   resolves. Mirrors .pricing-card's badge / name / price / feature-list /
   CTA layout so the page doesn't jump when the real cards arrive. */
const SubscriptionCardSkeleton = () => (
  <section className="sk-pricing-card" aria-hidden="true">
    <div className="sk-pricing__badge-row">
      <span
        className="sk"
        style={{ width: 110, height: 18, borderRadius: 999 }}
      />
    </div>
    <div className="sk-pricing__name">
      <span className="sk" style={{ width: 120, height: 22 }} />
    </div>
    <div className="sk-pricing__price">
      <span className="sk" style={{ width: 90, height: 32 }} />
      <span className="sk" style={{ width: 50, height: 14 }} />
    </div>
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className="sk-pricing__feature">
        <span
          className="sk"
          style={{ width: 14, height: 14, borderRadius: "50%", flexShrink: 0 }}
        />
        <span
          className="sk"
          style={{ width: `${65 + ((i * 13) % 25)}%`, height: 12 }}
        />
      </div>
    ))}
    <div className="sk-pricing__cta">
      <span
        className="sk"
        style={{ width: "100%", height: 44, borderRadius: 10 }}
      />
    </div>
  </section>
);

const SubscriptionMembership = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(subscriptionData());
  }, [dispatch]);

  const authUser = useSelector((state) => state.auth);
  const actualUser = authUser?.user;
  const user = useSelector((state) => state.auth?.user);
  const subscriptionState = useSelector((state) => state.subscriptionData);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // UI state
  const [billingCycle, setBillingCycle] = useState("year"); // Defaulting to annual per screenshot

  // Sliding pill refs for the billing toggle
  const monthBtnRef = useRef(null);
  const yearBtnRef = useRef(null);
  const [sliderStyle, setSliderStyle] = useState({});
  const sliderHasPlaced = useRef(false);

  useLayoutEffect(() => {
    const activeBtn =
      billingCycle === "month" ? monthBtnRef.current : yearBtnRef.current;
    if (activeBtn) {
      setSliderStyle({
        width: activeBtn.offsetWidth + "px",
        transform: `translateX(${activeBtn.offsetLeft}px)`,
        transition: sliderHasPlaced.current ? undefined : "none",
      });
      sliderHasPlaced.current = true;
    }
  }, [billingCycle]);

  const [submittingPlanName, setSubmittingPlanName] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState(null);

  const plans = subscriptionState?.subscriptionDataItems || [];
  const location = useLocation();
  const triggerSource = location.state?.source || "direct";

  const { trigger } = getTrackingData(triggerSource);

  usePageTracking("SUBSCRIPTION");

  useEffect(() => {
    dispatch(activityStart({ page_name: "SUBSCRIPTION", platform: "web" }));
    return () => {
      dispatch(activityEnd({ page_name: "SUBSCRIPTION" }));
    };
  }, [dispatch]);

  const formattedPlans = useMemo(() => {
    return plans
      .filter((plan) => plan.slug !== "free-trial" && plan.slug !== "pro-trial")
      .reduce((acc, plan) => {
        const existing = acc.find((p) => p.name === plan.name);

        if (existing) {
          if (plan.interval === "year") {
            existing.yearlyPriceId = plan.price_id;
            existing.yearlyPlanId = plan.id;
            existing.perYear = plan.price;
            existing.discountLabel = plan.discount
              ? `SAVE ${plan.discount}%`
              : null;
            // Calculate what monthly would cost over a year for the strikethrough text
            existing.monthlyEquivalentYearly = existing.monthlyPrice
              ? (existing.monthlyPrice * 12).toFixed(2)
              : null;
          }
        } else {
          acc.push({
            ...plan,
            displayName: plan.name,
            monthlyPlanId: plan.interval === "month" ? plan.id : null,
            monthlyPriceId: plan.interval === "month" ? plan.price_id : null,
            perMonth: plan.interval === "month" ? plan.price : 0,
            monthlyPrice: plan.interval === "month" ? plan.price : null,

            yearlyPlanId: plan.interval === "year" ? plan.id : null,
            yearlyPriceId: plan.interval === "year" ? plan.price_id : null,
            perYear: plan.interval === "year" ? plan.price : null,
            yearlyPrice: plan.interval === "year" ? plan.price : null,

            discountLabel:
              plan.interval === "year" && plan.discount
                ? `SAVE ${plan.discount}%`
                : null,

            features:
              typeof plan.features === "string"
                ? JSON.parse(plan.features || "[]").map((f) =>
                    typeof f === "string" ? f : f.name,
                  )
                : Array.isArray(plan.features)
                ? plan.features.map((f) => (typeof f === "string" ? f : f.name))
                : [],
            popular: plan.name === "Pro",
            // Pro+Newsletter gets the green theme below (see isGreenTheme).
            isNewsletter: plan.slug === "pro-newsletter",
          });
        }
        return acc;
      }, [])
      .sort((a, b) => {
        const order = {
          Free: 0,
          Pro: 1,
          "Pro + Newsletter": 2,
          Plus: 3,
        };
        return (order[a.name] ?? 999) - (order[b.name] ?? 999);
      });
  }, [plans]);

  useEffect(() => {
    if (!formattedPlans.length) return;
    const userInterval = user?.plan?.interval;
    if (userInterval) setBillingCycle(userInterval);
  }, [formattedPlans, user]);

  const canUpgradeToPlan = useMemo(() => {
    const currentPlanName = user?.plan?.name;
    const currentInterval = user?.plan?.interval;
    const currentSlug = user?.plan?.slug;

    // Plans are mutually exclusive (one active plan_id per user — see
    // mapmypay_plan_migration memory). Any switch is allowed except:
    //   • staying on the exact same plan + interval (nothing to do)
    //   • switching the same plan's interval from yearly → monthly
    //     (treated as a downgrade — handled via Stripe portal instead)
    // Plan tier hierarchy for upgrade-direction enforcement. Going to a HIGHER
    // tier (or the same tier with longer billing) is an "upgrade". Going to a
    // LOWER tier is a downgrade — user must cancel current sub first.
    const PLAN_TIER = { Plus: 1, Pro: 2, "Pro + Newsletter": 3 };

    return (targetPlanName, targetBillingCycle) => {
      // Free / no plan / leftover legacy free-trial users → can buy any paid plan
      if (
        !currentPlanName ||
        currentPlanName === "Free" ||
        currentSlug === "free-trial"
      ) {
        return targetPlanName !== "Free";
      }

      // Pro Trial users get a dedicated "Cancel trial" banner. Don't offer
      // plan-switching CTAs on the cards while the trial is active —
      // switching mid-trial cancels the trial and triggers immediate billing
      // on the new plan, which is rarely what the user wants.
      if (currentSlug === "pro-trial") {
        return false;
      }

      // Free is never an "upgrade" target.
      if (targetPlanName === "Free") return false;

      // Already on this exact plan + interval — nothing to upgrade to.
      if (
        currentPlanName === targetPlanName &&
        currentInterval === targetBillingCycle
      ) {
        return false;
      }

      // Block yearly → monthly of the SAME plan (treated as downgrade).
      if (
        currentPlanName === targetPlanName &&
        currentInterval === "year" &&
        targetBillingCycle === "month"
      ) {
        return false;
      }

      // Block plan-tier downgrades — e.g. Pro+Newsletter → Pro means losing
      // the newsletter feature. The user must cancel and resubscribe if they
      // actually want a lower tier.
      const currentRank = PLAN_TIER[currentPlanName] ?? 0;
      const targetRank = PLAN_TIER[targetPlanName] ?? 0;
      if (currentRank > targetRank) {
        return false;
      }

      // Otherwise valid: Plus (grandfathered) → Pro / Pro+Newsletter,
      // Pro → Pro+Newsletter, Pro Monthly → Pro Yearly, etc.
      return true;
    };
  }, [user?.plan?.name, user?.plan?.interval, user?.plan?.slug]);

  const hasExistingSubscription = useMemo(() => {
    return Boolean(
      user?.plan &&
        user.plan.name &&
        user.plan.name !== "Free" &&
        user.plan.slug !== "free-trial",
    );
  }, [user?.plan]);

  const handleStripeCheckout = async (payloadOverride = null, planName = null) => {
    const payload = payloadOverride || pendingPayload;
    if (!payload || !payload.priceId) return;

    setSubmittingPlanName(planName || selectedPlanForUpgrade?.name || null);
    try {
      const result = await dispatch(subscriptionPayment(payload)).unwrap();
      const checkoutUrl =
        result?.subscriptionPaymentItems?.url ||
        result?.url ||
        result?.data?.url;
      if (checkoutUrl) {
        // New subscription path → Stripe Checkout redirect. Redux refresh
        // happens naturally on /success after the user returns.
        window.location.href = checkoutUrl;
      } else {
        // Inline subscription-update path (existing sub being changed via
        // stripe.subscriptions.update on the backend). No redirect happens,
        // so the page stays mounted — refresh redux user so plan info,
        // current-plan badge, and upgrade buttons update immediately.
        await dispatch(refreshUserProfile());
      }
    } catch (error) {
      console.error("Checkout failed:", error);
    } finally {
      setSubmittingPlanName(null);
      setShowUpgradeModal(false);
      setPendingPayload(null);
    }
  };

  const onSubscribeClick = (plan) => {
    if (typeof window !== "undefined" && window.fbq) {
      const amount =
        billingCycle === "year" ? plan.yearlyPrice : plan.monthlyPrice;
      window.fbq("track", "InitiateCheckout", {
        content_name: `${plan.name} Plan`,
        value: Number(amount),
        currency: "USD",
      });
    }


    const priceId =
      billingCycle === "year" ? plan.yearlyPriceId : plan.monthlyPriceId;
    const selectedPlanId =
      billingCycle === "year" ? plan.yearlyPlanId : plan.monthlyPlanId;

    const payload = {
      priceId,
      customerEmail: user?.email,
      metadata: {
        userId: user?.id,
        plan_id: selectedPlanId,
        billingCycle,
        trigger_feature: trigger,
      },
    };

    if (hasExistingSubscription) {
      const isSamePlan =
        user?.plan?.name === plan.name && user?.plan?.interval === billingCycle;
      if (!isSamePlan) {
        setPendingPayload(payload);
        setSelectedPlanForUpgrade(plan);
        setShowUpgradeModal(true);
        return;
      }
    }
    handleStripeCheckout(payload, plan.name);
  };

  const toggleFaq = (index) => setOpenFaq(openFaq === index ? -1 : index);

  const faqs = [
    {
      q: "Can I cancel anytime?",
      a:
        "Yes. Cancel any time from Settings → Subscription. You'll keep access until the end of your current billing period — no questions, no retention calls.",
    },
    {
      q: "What happens after my trial?",
      a:
        "On day 4 of your trial, your card is charged $29.99 for a full year of Pro. You'll get an email reminder 24 hours before. Cancel anytime during the 3-day trial to avoid the charge.",
    },
    {
      q: "What's the difference between Pro and Pro + Newsletter?",
      a:
        "Pro unlocks unlimited use of every in-app feature. Pro + Newsletter adds the weekly MapMyPay Insider — curated high-pay listings, salary trend reports, and state-by-state market alerts delivered to your inbox.",
    },
    {
      q: "Will my old plan stay the same?",
      a:
        "If you're on the legacy $6.99 or $12.99 plan, you'll keep your existing features and price for as long as your subscription is active. Migrate to a new plan anytime from the migration screen in Settings.",
    },
    {
      q: "How is the take-home calculation done?",
      a:
        "We model federal, state, and local taxes (FICA, Medicare, SDI), then subtract median rent or mortgage for your selected city. Tax tables are refreshed each January; rent and home-price data are refreshed quarterly from public sources.",
    },
  ];

  const hasActivePaidSubscription = useMemo(() => {
    return (
      actualUser?.plan &&
      actualUser.plan.name !== "Free" &&
      actualUser.plan.slug !== "free-trial" &&
      actualUser.plan.subscription_status !== "cancelling" &&
      actualUser.plan.subscription_status !== "cancelled"
    );
  }, [actualUser?.plan]);

  const isCancelling = actualUser?.plan?.subscription_status === "cancelling";
  const subscriptionSource = actualUser?.plan?.subscription_source;
  const canSubscribe =
    !subscriptionSource ||
    subscriptionSource === "null" ||
    subscriptionSource === "free";
  const canUpgrade = subscriptionSource === "stripe";
  // Subscriptions assigned by an admin, purchased through the mobile app /
  // Google Play / App Store, or granted via a referral can't be managed from
  // the web. For these sources we hide both the Upgrade and Cancel buttons and
  // show an informational message instead.
  const isAdminUser = subscriptionSource === "admin";
  const isMobileUser = subscriptionSource === "mobile";
  const isGoogleUser = subscriptionSource === "google";
  const isAppleUser = subscriptionSource === "apple";
  const isReferralUser = subscriptionSource === "referral";
  // Any source whose subscription is managed outside the web app.
  const isUnmanagedSource =
    isAdminUser ||
    isMobileUser ||
    isGoogleUser ||
    isAppleUser ||
    isReferralUser;
  // Informational note shown in place of the Upgrade / Cancel buttons for
  // externally-managed subscriptions.
  const sourceNote = isAdminUser
    ? "Your premium subscription is assigned by an administrator. Subscription upgrades or cancellations are not available here."
    : isMobileUser
    ? "Your subscription was purchased through the mobile app. Please manage your subscription through your mobile device."
    : isGoogleUser
    ? "Your subscription was purchased through Google Play. Please manage your subscription through the Google Play Store on your Android device."
    : isAppleUser
    ? "Your subscription was purchased through the App Store. Please manage your subscription through Settings on your Apple device."
    : isReferralUser
    ? "Your premium access was granted through a referral. Subscription upgrades or cancellations are not available for referral-based plans."
    : null;
  // Cancel is available for an active paid plan, EXCEPT externally-managed
  // subscriptions (admin / mobile / Google / Apple / referral), which must be
  // managed elsewhere.
  const canCancel =
    hasActivePaidSubscription && !isCancelling && !isUnmanagedSource;
  // The paid Pro Trial is once-per-user. Anyone already on a paid plan
  // (active OR cancelling — they still have paid access through period end),
  // OR who has previously claimed the trial, OR who is currently on Pro
  // Trial should NOT see the "Start 3-day trial" CTA.
  const isCancellingAnyPlan =
    actualUser?.subscription_status === "cancelling" ||
    actualUser?.plan?.subscription_status === "cancelling";
  const isTrialEligible =
    !hasActivePaidSubscription &&
    !isCancellingAnyPlan &&
    !actualUser?.has_used_pro_trial &&
    actualUser?.plan?.slug !== "pro-trial";

  // Guided tour — auto-starts on the user's first visit and is replayable from
  // the header's tour button (pageKey "subscription"). Steps receive
  // setBillingCycle so the tour can drive the Monthly/Annual toggle as it goes.
  const { isOpen: isTourOpen, finish: finishTour } = useTour("subscription");

  return (
    <div className="subscription-membership-page">
      <NoIndexSEO title={seoConfig.subscription.title} />
      <GuidedTour
        isOpen={isTourOpen}
        steps={getSubscriptionTourSteps(setBillingCycle)}
        onDone={finishTour}
      />

      <div className="app-page-title">
        <div className="page-title-heading d-flex align-items-center">
          <FiArrowLeft
            size={40}
            className="icon cursor-pointer"
            onClick={() => navigate(-1)}
          />
          <h3 className="page-title">Subscription Membership</h3>
        </div>
      </div>

      <div className="mobile-wrapper">
        <div className="hero-promo-wrapper">
          <section className="hero-section">
            <span className="logo-text">MAPMYPAY PRO</span>
            <h1>Real take-home pay, unlimited cities</h1>
            <p>
              Stop guessing what a city actually pays. See the math after taxes
              and housing for any nursing role in 21,000+ U.S. cities.
            </p>
          </section>

          {isTrialEligible && (
            <section className="trial-card promo-card" data-tour="sub-start">
              <span className="small-title">★ START HERE</span>
              <h2>Try Pro free for 3 days</h2>
              <p>
                Get full app access free for <strong>3 days</strong>. No charge
                today. On day 4, $29.99/year will be charged to your payment
                method unless canceled before the trial ends.
              </p>
              <button
                className="primary-btn blue"
                onClick={() => navigate("/trial", { state: { billingCycle, source: triggerSource } })}
              >
                Start 3-day free trial · $29.99/year
              </button>
              <small>Free for 3 days. $29.99/year charged on day 4 unless canceled.</small>
            </section>
          )}

          {/* Pro Trial users don't match any displayed plan card (the card
              itself is filtered out of /plans-with-features), so without this
              banner there's nothing on the page indicating their current
              state. Copy switches when the trial is in 'cancelling' state. */}
          {actualUser?.plan?.slug === "pro-trial" &&
            (() => {
              const isCancellingTrial =
                actualUser?.subscription_status === "cancelling" ||
                actualUser?.plan?.subscription_status === "cancelling";
              const accessUntil =
                actualUser?.plan_expiry_date || actualUser?.trial_ends_at;
              const accessUntilFmt = accessUntil
                ? new Date(accessUntil).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : null;

              return (
                <section className="trial-card promo-card current-trial-banner">
                  <span className="small-title">
                    {isCancellingTrial ? "★ TRIAL CANCELLED" : "★ CURRENT PLAN"}
                  </span>
                  <h2>
                    {isCancellingTrial
                      ? "Your Pro Trial has been cancelled"
                      : "Your Pro Trial is active"}
                  </h2>
                  <p>
                    {isCancellingTrial ? (
                      accessUntilFmt ? (
                        <>
                          You'll keep full access through{" "}
                          <strong>{accessUntilFmt}</strong>.{" "}
                          <strong>No charge</strong> will be made when the trial
                          ends — you'll move to Free automatically.
                        </>
                      ) : (
                        <>
                          You'll keep full access through the end of your trial.{" "}
                          <strong>No charge</strong> will be made — you'll move
                          to Free automatically.
                        </>
                      )
                    ) : accessUntilFmt ? (
                      <>
                        Trial ends on <strong>{accessUntilFmt}</strong>. You'll
                        be charged <strong>$29.99/year</strong> on day 4 unless
                        you cancel.
                      </>
                    ) : (
                      <>
                        You'll be charged <strong>$29.99/year</strong> after the
                        3-day trial unless you cancel.
                      </>
                    )}
                  </p>
                  {!isCancellingTrial && canCancel && (
                    <button
                      className="primary-btn danger"
                      onClick={() => setShowCancelModal(true)}
                    >
                      Cancel trial
                    </button>
                  )}
                  {!isCancellingTrial && isUnmanagedSource && (
                    <small className="plan-footer-note subscription-source-note">
                      {sourceNote}
                    </small>
                  )}
                  <small>
                    {isCancellingTrial
                      ? "Your $2.99 trial fee from sign-up is non-refundable."
                      : "Cancel before day 4 to avoid the $29.99 charge. Your $2.99 trial fee is non-refundable."}
                  </small>
                </section>
              );
            })()}

          {/* Grandfathered Plus users — Plus is filtered out of the plan
              cards below, so without this banner there's nothing on the page
              telling them they're on Plus or how to cancel. */}
          {actualUser?.plan?.slug === "plus" &&
            (() => {
              const isCancellingPlus =
                actualUser?.subscription_status === "cancelling" ||
                actualUser?.plan?.subscription_status === "cancelling";
              const planInterval = actualUser?.plan?.interval; // 'month' or 'year'
              const planPrice = actualUser?.plan?.price;
              const intervalLabel = planInterval === "year" ? "year" : "month";
              const accessUntilFmt = actualUser?.plan_expiry_date
                ? new Date(actualUser.plan_expiry_date).toLocaleDateString(
                    undefined,
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    },
                  )
                : null;

              return (
                <section className="trial-card promo-card current-trial-banner">
                  <span className="small-title">
                    {isCancellingPlus ? "★ PLUS CANCELLED" : "★ CURRENT PLAN"}
                  </span>
                  <h2>
                    {isCancellingPlus
                      ? "Your Plus plan has been cancelled"
                      : "Plus · Grandfathered plan"}
                  </h2>
                  <p>
                    {isCancellingPlus ? (
                      accessUntilFmt ? (
                        <>
                          You'll keep Plus access through{" "}
                          <strong>{accessUntilFmt}</strong>. After that you'll
                          move to Free automatically.{" "}
                          <strong>Plus cannot be re-purchased</strong> once it
                          ends.
                        </>
                      ) : (
                        <>
                          You'll keep Plus access through your current billing
                          period. After that you'll move to Free automatically.{" "}
                          <strong>Plus cannot be re-purchased</strong> once it
                          ends.
                        </>
                      )
                    ) : (
                      <>
                        You're on <strong>Plus</strong>
                        {planPrice ? (
                          <>
                            {" · "}
                            <strong>
                              ${planPrice}/{intervalLabel}
                            </strong>
                          </>
                        ) : null}
                        . Plus is no longer offered to new subscribers — your
                        plan is grandfathered. You can keep it indefinitely, or
                        upgrade to Pro / Pro + Newsletter below. If you cancel,
                        Plus cannot be re-purchased.
                      </>
                    )}
                  </p>
                  {!isCancellingPlus && canCancel && (
                    <button
                      className="primary-btn danger"
                      onClick={() => setShowCancelModal(true)}
                    >
                      Cancel Plus
                    </button>
                  )}
                  {!isCancellingPlus && isUnmanagedSource && (
                    <small className="plan-footer-note subscription-source-note">
                      {sourceNote}
                    </small>
                  )}
                </section>
              );
            })()}

          {/* Generic "subscription cancelled" banner for Pro and Pro+Newsletter
              users in 'cancelling' state. Pro Trial and Plus have their own
              dedicated banners above; this catches the remaining paid tiers. */}
          {isCancellingAnyPlan &&
            actualUser?.plan?.slug !== "pro-trial" &&
            actualUser?.plan?.slug !== "plus" &&
            actualUser?.plan?.name &&
            actualUser?.plan?.name !== "Free" &&
            (() => {
              const planLabel = actualUser?.plan?.name;
              const intervalLabel =
                actualUser?.plan?.interval === "year" ? "yearly" : "monthly";
              const accessUntilFmt = actualUser?.plan_expiry_date
                ? new Date(actualUser.plan_expiry_date).toLocaleDateString(
                    undefined,
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    },
                  )
                : null;

              return (
                <section className="trial-card promo-card current-trial-banner">
                  <span className="small-title">★ SUBSCRIPTION CANCELLED</span>
                  <h2>Your {planLabel} subscription has been cancelled</h2>
                  <p>
                    {accessUntilFmt ? (
                      <>
                        You'll keep your {intervalLabel} {planLabel} access
                        through <strong>{accessUntilFmt}</strong>. After that
                        you'll move to <strong>Free</strong> automatically —{" "}
                        <strong>no charge</strong> will be made.
                      </>
                    ) : (
                      <>
                        You'll keep access through the end of your current
                        billing period. After that you'll move to{" "}
                        <strong>Free</strong> automatically —{" "}
                        <strong>no charge</strong> will be made.
                      </>
                    )}
                  </p>
                  <small>
                    Changed your mind? Re-subscribe any time before access ends
                    to keep your plan without interruption.
                  </small>
                </section>
              );
            })()}
        </div>

        <div className="divider-container">
          <span className="divider-text">COMPARE PLANS</span>
        </div>

        <div className="billing-toggle" data-tour="sub-toggle">
          <div
            className="toggle-slider"
            style={sliderStyle}
            aria-hidden="true"
          />
          <button
            ref={monthBtnRef}
            className={`toggle-btn ${billingCycle === "month" ? "active" : ""}`}
            onClick={() => setBillingCycle("month")}
          >
            Monthly
          </button>
          <button
            ref={yearBtnRef}
            className={`toggle-btn ${billingCycle === "year" ? "active" : ""}`}
            onClick={() => setBillingCycle("year")}
          >
            Annual <span className="save-badge">SAVE 17%</span>
          </button>
        </div>

        <div className="plans-grid">
          {subscriptionState?.isLoading && formattedPlans.length === 0
            ? [0, 1, 2].map((i) => <SubscriptionCardSkeleton key={`sk-${i}`} />)
            : null}
          {formattedPlans.map((plan, idx) => {
            // Match BOTH plan name AND the interval being displayed (which
            // tracks the Monthly/Annual toggle). Pro and Pro+Newsletter each
            // share one name across monthly/yearly rows, so a name-only check
            // would mark "CURRENT PLAN" on both toggle states. Free has no
            // billing interval — skip the interval check for it.
            const isCurrentPlan =
              user?.plan?.name === plan.name &&
              (plan.name === "Free" || user?.plan?.interval === billingCycle);
            const isBlueTheme = plan.popular && !plan.isNewsletter;
            const isGreenTheme = plan.isNewsletter;

            const handleCardClick = (e) => {
              if (e.target.closest("button")) return;

              if (isCurrentPlan && actualUser?.plan?.interval === billingCycle && canCancel) {
                setShowCancelModal(true);
                return;
              }

              if (isBlueTheme && !isCancelling) {
                if (isTrialEligible) {
                  if (billingCycle === "month") {
                    onSubscribeClick(plan);
                  } else {
                    navigate("/trial", { state: { billingCycle, source: triggerSource } });
                  }
                  return;
                }
                if (
                  !isUnmanagedSource &&
                  canUpgradeToPlan(plan.name, billingCycle)
                ) {
                  onSubscribeClick(plan);
                  return;
                }
              }

              if (
                isGreenTheme &&
                !isCancelling &&
                !isUnmanagedSource &&
                !(hasActivePaidSubscription && isCurrentPlan && actualUser?.plan?.interval === billingCycle && canUpgrade) &&
                canUpgradeToPlan(plan.name, billingCycle)
              ) {
                onSubscribeClick(plan);
                return;
              }

              if (!isBlueTheme && !isGreenTheme && plan.name !== "Free" && !isCancelling) {
                if (canUpgradeToPlan(plan.name, billingCycle)) {
                  if (isTrialEligible && canSubscribe) {
                    navigate("/trial", { state: { billingCycle, source: triggerSource } });
                  } else if (canUpgrade) {
                    onSubscribeClick(plan);
                  }
                }
              }
            };

            // Tour hook per tier — Free / Pro (blue) / Pro + Newsletter (green).
            const tourId =
              plan.name === "Free"
                ? "sub-free"
                : isGreenTheme
                  ? "sub-news"
                  : isBlueTheme
                    ? "sub-pro"
                    : undefined;

            return (
              <section
                key={`${plan.name}-${billingCycle}`}
                data-tour={tourId}
                className={`pricing-card ${isCurrentPlan ? "current" : ""} ${
                  isBlueTheme ? "theme-blue" : ""
                } ${isGreenTheme ? "theme-green" : ""}`}
                style={{
                  animationDelay: `${idx * 80}ms`,
                  cursor: plan.name !== "Free" ? "pointer" : "default",
                }}
                onClick={handleCardClick}
              >
                {/* Badges */}
                {isBlueTheme && (
                  <span className="top-badge popular-badge">MOST POPULAR</span>
                )}
                <div className="card-top-badges">
                  {isGreenTheme && (
                    <span className="top-badge green-badge">
                      <span className="badge-icon">✉</span> INCLUDES NEWSLETTER
                    </span>
                  )}
                  <div className="spacer"></div>
                </div>

                <div className="plan-name-row">
                  <h3>{plan.displayName}</h3>
                  {isCurrentPlan && (
                    <span className="top-badge current-badge">
                      CURRENT PLAN
                    </span>
                  )}
                </div>

                <div className="price-area" key={billingCycle}>
                  <h2>
                    $
                    {billingCycle === "year" && plan.perYear
                      ? plan.perYear
                      : plan.perMonth}
                  </h2>
                  {plan.name !== "Free" ? (
                    <div className="price-suffix">
                      <p>/{billingCycle === "year" ? "year" : "month"}</p>
                      {billingCycle === "year" && plan.discountLabel && (
                        <span className="discount-tag">
                          {plan.discountLabel}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="price-suffix">
                      <p>/forever</p>
                    </div>
                  )}
                </div>

                {billingCycle === "year" &&
                  plan.monthlyEquivalentYearly &&
                  plan.name !== "Free" && (
                    <div className="strikethrough-price">
                      <span className="amount">
                        ${plan.monthlyEquivalentYearly}/year
                      </span>{" "}
                      if billed monthly
                    </div>
                  )}

                {isGreenTheme && (
                  <p className="newsletter-blurb">
                    Everything in Pro, plus the{" "}
                    <strong>MapMyPay Insider</strong> weekly newsletter
                    delivered straight to your inbox.
                  </p>
                )}

                <ul className="feature-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <svg
                        className="check-icon"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="card-actions">
                  {isCurrentPlan &&
                    actualUser?.plan?.interval === billingCycle &&
                    canCancel && (
                      <button
                        className="primary-btn danger"
                        onClick={() => setShowCancelModal(true)}
                      >
                        Cancel Subscription
                      </button>
                    )}

                  {/* Admin-assigned / mobile-purchased subs can't be managed
                      from the web — show a message instead of the buttons. */}
                  {isCurrentPlan &&
                    hasActivePaidSubscription &&
                    isUnmanagedSource && (
                      <small className="plan-footer-note subscription-source-note">
                        {sourceNote}
                      </small>
                    )}

                  {/* Pro card CTA — branches on billing cycle and trial eligibility */}
                  {isBlueTheme && isTrialEligible && !isCancelling && (
                    <>
                      <button
                        className="primary-btn blue full-width"
                        onClick={() =>
                          billingCycle === "month"
                            ? onSubscribeClick(plan)
                            : navigate("/trial", { state: { billingCycle, source: triggerSource } })
                        }
                        disabled={billingCycle === "month" && submittingPlanName === plan.name}
                      >
                        {billingCycle === "month"
                          ? (submittingPlanName === plan.name ? "Opening Secure Payment..." : `Get Pro · $${plan.perMonth || "2.99"}/month`)
                          : `Start 3-day free trial · $${plan.perYear || "29.99"}/year`}
                      </button>
                      <small className="plan-footer-note">
                        {billingCycle === "month"
                          ? `$${plan.perMonth || "2.99"}/month. Cancel anytime in Settings.`
                          : `After trial, $${plan.perYear || "29.99"}/year. Cancel anytime in Settings.`}
                      </small>
                    </>
                  )}

                  {/* Already-paid or trial-used user → show Upgrade if applicable.
                      Admin-assigned / mobile-purchased subs can't upgrade here. */}
                  {isBlueTheme &&
                    !isTrialEligible &&
                    !isCancelling &&
                    !isUnmanagedSource &&
                    canUpgradeToPlan(plan.name, billingCycle) && (
                      <button
                        className="primary-btn blue full-width"
                        onClick={() => onSubscribeClick(plan)}
                        disabled={submittingPlanName === plan.name}
                      >
                        {submittingPlanName === plan.name ? "Processing..." : "UPGRADE NOW"}
                      </button>
                    )}

                  {isGreenTheme &&
                    !isCancelling &&
                    !isUnmanagedSource &&
                    !(
                      hasActivePaidSubscription &&
                      isCurrentPlan &&
                      actualUser?.plan?.interval === billingCycle &&
                      canUpgrade
                    ) &&
                    canUpgradeToPlan(plan.name, billingCycle) && (
                      <button
                        className="primary-btn green full-width"
                        onClick={() => onSubscribeClick(plan)}
                        disabled={submittingPlanName === plan.name}
                      >
                        {submittingPlanName === plan.name
                          ? "Processing..."
                          : "Upgrade to Pro + Newsletter"}
                      </button>
                    )}

                  {!isBlueTheme &&
                    !isGreenTheme &&
                    plan.name !== "Free" &&
                    canUpgradeToPlan(plan.name, billingCycle) &&
                    !isCancelling &&
                    canSubscribe &&
                    isTrialEligible && (
                      <button
                        className="primary-btn blue"
                        onClick={() => navigate("/trial", { state: { billingCycle, source: triggerSource } })}
                      >
                        {`Start 3-day trial · $${plan.perMonth || "2.99"}`}
                      </button>
                    )}

                  {!isBlueTheme &&
                    !isGreenTheme &&
                    plan.name !== "Free" &&
                    canUpgradeToPlan(plan.name, billingCycle) &&
                    !isCancelling &&
                    canUpgrade && (
                      <button
                        className="primary-btn blue"
                        onClick={() => onSubscribeClick(plan)}
                        disabled={submittingPlanName === plan.name}
                      >
                        {submittingPlanName === plan.name ? "Processing..." : "UPGRADE NOW"}
                      </button>
                    )}

                  {!isBlueTheme &&
                    !isGreenTheme &&
                    plan.name !== "Free" &&
                    !isCurrentPlan && (
                      <small className="plan-footer-note">
                        {billingCycle === "year"
                          ? `Renews at $${plan.perYear}/year. Cancel anytime in Settings.`
                          : `After trial, $${plan.perMonth}/month. Cancel anytime in Settings.`}
                      </small>
                    )}
                </div>
              </section>
            );
          })}
        </div>

        <section className="stats-grid" data-tour="sub-trust">
          <div className="stat-box">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <h4>9,000+</h4>
            <p>nurses</p>
          </div>
          <div className="stat-box">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <h4>21,000+</h4>
            <p>cities</p>
          </div>
          <div className="stat-box">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.59-9.22l-5.3 5.3"></path>
            </svg>
            <h4>Updated</h4>
            <p>weekly</p>
          </div>
        </section>

        <section className="faq-section" data-tour="sub-faq">
          <h3>Frequently asked</h3>
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`faq-item ${openFaq === index ? "open" : ""}`}
              onClick={() => toggleFaq(index)}
            >
              <div className="faq-question">
                <span>{faq.q}</span>
                <span className="chevron">
                  <FiChevronDown />
                </span>
              </div>
              <div className="faq-answer">
                <p>{faq.a}</p>
              </div>
            </div>
          ))}
        </section>
      </div>

      <CancelSubscriptionModal
        show={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={() => setShowCancelModal(false)}
      />
      <UpgradeConfirmationModal
        show={showUpgradeModal}
        onHide={() => {
          setShowUpgradeModal(false);
          setPendingPayload(null);
          setSelectedPlanForUpgrade(null);
        }}
        onConfirm={() => {
          if (pendingPayload) handleStripeCheckout(pendingPayload, selectedPlanForUpgrade?.name);
          else setShowUpgradeModal(false);
        }}
        currentPlan={user?.plan?.name}
        currentInterval={user?.plan?.interval}
        selectedPlanName={selectedPlanForUpgrade?.name}
        billingCycle={billingCycle}
        userId={user?.id}
        priceId={pendingPayload?.priceId}
      />
    </div>
  );
};

export default SubscriptionMembership;
